/* App shell: offline banner, outbox chip, enrolment, and the router.
 *
 * Sync policy (03 §3.3): drain the outbox on launch, on app focus, and after a
 * session. Keeping the outbox minutes-small is also the mitigation for risk R1
 * (WebKit evicting IndexedDB) — nothing above the outbox is unrecoverable,
 * because the server is authoritative for everything else.
 *
 * Navigation is CR-021. The app used to encode one depth-1 stack in six
 * mutually-exclusive booleans and had no URLs at all, so browser Back left the
 * app and a reload lost your place. It is now one parsed hash route, and the
 * app opens on the work rather than on a plan of it.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api, ApiFailure, clearToken, getToken, storeToken,
  type MeResponse, type TodayPlan, type UnitDto, type QuizAttemptResult,
} from './api';
import { outboxCount } from './db';
import { drainOutbox, fetchAndCachePlan, getCachedPlan, isPlanStale } from './sync';
import { MapScreen } from './screens/map';
import { ReferenceScreen } from './screens/reference';
import { UnitScreen } from './screens/unit';
import { DrillScreen } from './screens/drill';
import { QuizScreen, type QuizAnswer } from './screens/quiz';
import { ProgressScreen } from './screens/progress';
import { SettingsScreen } from './screens/settings';
import { TutorScreen } from './screens/tutor';
import { cachedUnit, getLastSyncAt } from './sync';
import { submitQuizAttempt, drainQuizAttempts } from './quiz-submit';
import { latestDraft } from './build-store';
import {
  DEFAULT_ROUTE, TABS, formatRoute, parseRoute, showsTabs, tabOf, type Route,
} from './router';

type Phase = 'loading' | 'enroll' | 'app';

/** The hash is the single source of truth for where you are. */
function useRoute(): [Route, (r: Route, replace?: boolean) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onHash = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = useCallback((next: Route, replace = false) => {
    const hash = formatRoute(next);
    if (window.location.hash === hash) return;
    if (replace) window.history.replaceState(null, '', hash);
    else window.location.hash = hash;
    setRoute(next);
  }, []);
  return [route, go];
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

function Banner({ online, pending, stale }: { online: boolean; pending: number; stale: boolean }) {
  if (!online) {
    return (
      <div className="banner banner--offline" role="status">
        Offline — everything you do is saved and will sync
        {pending > 0 ? ` · ${pending} queued` : ''}
      </div>
    );
  }
  if (pending > 0) {
    return <div className="banner" role="status">{pending} waiting to sync</div>;
  }
  if (stale) {
    return <div className="banner" role="status">Showing yesterday's plan — refreshing</div>;
  }
  return null;
}

function EnrollScreen({ onEnrolled }: { onEnrolled: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!code.trim() || busy) return;
      setBusy(true);
      setError(null);
      try {
        const res = await api.enroll(code, 'iPhone');
        storeToken(res.device_token, res.device_id);
        onEnrolled();
      } catch (err) {
        setError(
          err instanceof ApiFailure
            ? err.code === 'OFFLINE'
              ? 'No connection — enrolment needs the server once.'
              : err.message
            : 'Something went wrong.',
        );
        setBusy(false);
      }
    },
    [code, busy, onEnrolled],
  );

  return (
    <main className="screen">
      <p className="eyebrow">Basecamp</p>
      <h1>Enrol this iPhone</h1>
      <p>
        ASCENT is for exactly one learner. Read the one-time code from the server
        console and type it in below — it is single-use and expires after 24 hours.
      </p>
      <form onSubmit={submit}>
        <label htmlFor="code">Enrolment code</label>
        <input
          id="code" type="text" inputMode="text" autoCapitalize="characters"
          autoCorrect="off" autoComplete="off" spellCheck={false}
          placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
          value={code} onChange={(e) => setCode(e.target.value)} disabled={busy}
        />
        {error ? <p className="error">{error}</p> : null}
        <p className="hint">On the server: <code>ascent-cli issue-enrollment-code</code></p>
        <div style={{ marginTop: '1.25rem' }}>
          <button type="submit" disabled={busy || code.trim().length === 0}>
            {busy ? 'Enrolling…' : 'Enrol'}
          </button>
        </div>
      </form>
      <div className="spacer" />
      <p className="footnote">
        The code is a credential. It is printed to the server console only — never
        sent by chat, email or commit.
      </p>
    </main>
  );
}

export default function App() {
  const online = useOnline();
  const [route, go] = useRoute();
  const [phase, setPhase] = useState<Phase>('loading');
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [quizUnit, setQuizUnit] = useState<UnitDto | null>(null);
  const [quizResult, setQuizResult] = useState<QuizAttemptResult | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [plan, setPlan] = useState<TodayPlan | null>(null);
  const [pending, setPending] = useState(0);
  const [stale, setStale] = useState(false);
  const [resolving, setResolving] = useState(false);

  const refresh = useCallback(async () => {
    setPending(await outboxCount());
    setLastSyncAt(await getLastSyncAt());
    const cached = await getCachedPlan();
    if (cached) {
      setPlan(cached);
      setStale(await isPlanStale(cached));
    }
  }, []);

  const syncAndLoad = useCallback(async () => {
    try {
      await drainOutbox();
      await drainQuizAttempts();
    } catch {
      // A failed drain must never block the UI; the next focus retries.
    }
    try {
      const fresh = await fetchAndCachePlan();
      if (fresh) {
        setPlan(fresh);
        setStale(await isPlanStale(fresh));
      }
    } catch {
      /* offline: the cached plan stays on screen */
    }
    setPending(await outboxCount());
    setLastSyncAt(await getLastSyncAt());
  }, []);

  const load = useCallback(async () => {
    await refresh();
    if (!getToken()) {
      setPhase('enroll');
      return;
    }
    try {
      setMe(await api.me());
      setPhase('app');
    } catch (err) {
      if (err instanceof ApiFailure && (err.status === 401 || err.status === 403)) {
        clearToken();
        setPhase('enroll');
        return;
      }
      setPhase('app'); // offline: the cached shell still works
    }
    void syncAndLoad();
  }, [refresh, syncAndLoad]);

  useEffect(() => { void load(); }, [load]);

  // Sync on app focus and when connectivity returns.
  useEffect(() => {
    if (phase !== 'app') return;
    const onFocus = () => { if (navigator.onLine) void syncAndLoad(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onFocus();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
    };
  }, [phase, syncAndLoad]);

  /* Launch resolves to the work (CR-021): the task touched most recently, else
   * the first build unit the release offers. Replaced rather than pushed, so
   * Back does not bounce through the empty Build route. */
  useEffect(() => {
    if (phase !== 'app' || route.name !== 'build' || route.unitId || resolving) return;
    setResolving(true);
    void (async () => {
      try {
        const draft = await latestDraft();
        if (draft) { go({ name: 'build', unitId: draft.unitId }, true); return; }
        const atlas = await api.atlas();
        for (const t of atlas.topics) {
          const build = t.units.find((u) => u.unit_type === 'build');
          if (build) { go({ name: 'build', unitId: build.unit_id }, true); return; }
        }
      } catch {
        /* offline with no draft: the Build panel below explains itself */
      } finally {
        setResolving(false);
      }
    })();
  }, [phase, route, resolving, go]);

  const openUnit = useCallback(async (unitId: string) => {
    // A quiz opens its player, a build task is the Build surface, and everything
    // else is reference reading.
    const unit = await cachedUnit(unitId);
    if (unit?.type === 'quiz') { go({ name: 'quiz', unitId }); return; }
    if (unit?.type === 'build') { go({ name: 'build', unitId }); return; }
    go({ name: 'reference', unitId });
  }, [go]);

  const finishQuiz = useCallback(async (answers: QuizAnswer[], startedAt: string) => {
    if (!quizUnit) return;
    const result = await submitQuizAttempt(quizUnit.id, quizUnit.version, answers, startedAt);
    setQuizResult(result);
    setQuizUnit(null);
    go({ name: 'progress' });
    await refresh();
    if (navigator.onLine) void syncAndLoad();
  }, [quizUnit, refresh, syncAndLoad, go]);

  // The quiz player needs the Unit itself; the route only names it.
  useEffect(() => {
    if (route.name !== 'quiz') { setQuizUnit(null); return; }
    void (async () => {
      const unit = await cachedUnit(route.unitId);
      if (unit) { setQuizResult(null); setQuizUnit(unit); }
    })();
  }, [route]);

  const back = useCallback(() => {
    if (window.history.length > 1) window.history.back();
    else go(DEFAULT_ROUTE);
  }, [go]);

  const dueDrills = plan?.drills.queue.length ?? 0;

  let screen: JSX.Element;
  if (phase === 'loading') {
    screen = <main className="screen"><p className="eyebrow">ASCENT</p></main>;
  } else if (phase === 'enroll') {
    screen = <EnrollScreen onEnrolled={() => void load()} />;
  } else if (route.name === 'settings') {
    screen = (
      <SettingsScreen me={me} lastSyncAt={lastSyncAt} onBack={back} onSyncNow={syncAndLoad} />
    );
  } else if (route.name === 'drill') {
    screen = (
      <DrillScreen
        queue={plan?.drills.queue ?? []}
        onDone={() => { go({ name: 'build', unitId: null }); void refresh(); }}
        onOpenSettings={() => go({ name: 'settings' })}
      />
    );
  } else if (route.name === 'quiz') {
    screen = quizUnit ? (
      <QuizScreen
        unit={quizUnit}
        onSubmit={finishQuiz}
        onCancel={back}
        onOpenSettings={() => go({ name: 'settings' })}
      />
    ) : (
      <main className="screen">
        <p className="eyebrow">Quiz</p>
        <div className="skeleton skeleton--block" />
      </main>
    );
  } else if (route.name === 'build') {
    screen = route.unitId ? (
      <UnitScreen
        unitId={route.unitId}
        onBack={() => { go({ name: 'map', domainId: null }); void refresh(); }}
      />
    ) : (
      <main className="screen">
        <p className="eyebrow">Build</p>
        <h1>{resolving ? 'Finding your task…' : 'Nothing to build yet'}</h1>
        {!resolving ? (
          <p>
            No build task is in the active release. Open the Map to pick up a
            lesson or a quiz in the meantime.
          </p>
        ) : null}
        <div style={{ marginTop: '1rem' }}>
          <button type="button" onClick={() => go({ name: 'map', domainId: null })}>
            Open the Map
          </button>
        </div>
        {dueDrills > 0 ? (
          <p className="hint">
            {dueDrills} drill cards are due — off the daily path now, but still here
            if you want them.{' '}
            <button type="button" className="linkish" onClick={() => go({ name: 'drill' })}>
              Run them
            </button>
          </p>
        ) : null}
      </main>
    );
  } else if (route.name === 'map') {
    screen = (
      <MapScreen
        domainId={route.domainId}
        onOpenDomain={(id) => go({ name: 'map', domainId: id })}
        onOpenUnit={(id) => void openUnit(id)}
      />
    );
  } else if (route.name === 'reference') {
    screen = route.unitId
      ? <UnitScreen unitId={route.unitId} onBack={back} />
      : <ReferenceScreen onOpenUnit={(id) => void openUnit(id)} />;
  } else if (route.name === 'tutor') {
    screen = <TutorScreen onOpenSettings={() => go({ name: 'settings' })} />;
  } else {
    screen = (
      <ProgressScreen
        onOpenTopic={() => go({ name: 'map', domainId: null })}
        onOpenSettings={() => go({ name: 'settings' })}
      />
    );
  }

  const activeTab = tabOf(route);
  const tabRoute = (name: string): Route =>
    name === 'build' ? { name: 'build', unitId: null }
      : name === 'map' ? { name: 'map', domainId: null }
        : name === 'reference' ? { name: 'reference', unitId: null }
          : name === 'tutor' ? { name: 'tutor', threadId: null }
            : { name: 'progress' };

  return (
    <>
      <Banner online={online} pending={pending} stale={stale} />
      {screen}
      {phase === 'app' && showsTabs(route) ? (
        <nav className="tabs" aria-label="Main">
          {TABS.map((t) => (
            <button
              key={t.name}
              type="button"
              className={`tabs__btn ${activeTab === t.name ? 'tabs__btn--on' : ''}`}
              onClick={() => go(tabRoute(t.name))}
            >
              {t.label}
            </button>
          ))}
        </nav>
      ) : null}
    </>
  );
}
